import { notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextAreaField } from 'src/components/Form/TextAreaField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { Modal } from 'src/components/Modal/Modal';
import { AgentLoanApplicationsStore } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { addModalRenderer, ModalRendererProps } from 'src/utils/ModalRenderer';

type DeclinePartnerApplicationDialogProps = ModalRendererProps<{
    applicationId: string | number;
}>;

const DeclinePartnerApplicationDialogFn = observer(
    ({
        applicationId,
        onSuccess,
        ...props
    }: DeclinePartnerApplicationDialogProps) => {
        const { t } = useTranslation();

        useEffect(() => {
            return () => {
                store.declineLoader.reset();
            };
        }, []);

        const fields = FormHelper.getFieldDataItems(
            store.declineLoader.errorData as ConstraintViolationError,
        );

        const onFinish = async (
            values: DeclinePartnerApplicationFormValues,
        ) => {
            await store.decline(String(applicationId), values.reason);
            if (!store.declineLoader.hasError) {
                notification.success({
                    message: t(
                        'App.DeclinePartnerApplicationDialog.DeclineSuccess',
                    ),
                });
                onSuccess?.();
            } else {
                return Promise.reject();
            }
        };

        return (
            <Modal
                title={t('App.DeclinePartnerApplicationDialog.Title')}
                okText={t('App.DeclinePartnerApplicationDialog.Confirm')}
                isLoading={store.declineLoader.isLoading}
                formProps={{ fields }}
                onFinish={onFinish}
                {...props}
            >
                <TextAreaField
                    name={formNames.getPath('reason')}
                    label={t('App.DeclinePartnerApplicationDialog.Reason')}
                    rules={[ValidationRules.required]}
                />
            </Modal>
        );
    },
);

type DeclinePartnerApplicationFormValues = {
    reason: string;
};

const formNames =
    FormHelper.createNameProvider<DeclinePartnerApplicationFormValues>();
const store = AgentLoanApplicationsStore;

export const DeclinePartnerApplicationDialog = addModalRenderer(
    DeclinePartnerApplicationDialogFn,
);
